import { http } from '@/config';
import type {
  Notification,
  PaginatedNotificationList,
  PaginatedSubscriptionCreateDeleteList,
  SubscriptionCreateDelete,
} from '@/types/swagger';
import axios from 'axios';
import z from 'zod';

import {
  ACTIONS_URL,
  EMAIL_SUBSCRIPTIONS_URL,
  IN_APP_BY_ID_URL,
  IN_APP_SUBSCRIPTIONS_URL,
  IN_APP_URL,
} from './endpoints';

const ActionsSchema = z.object({
  action_name: z.string(),
  action_code: z.string(),
  tooltip: z.string(),
});

const TopicsSchema = z.object({
  topic_name: z.string(),
  actions: z.array(ActionsSchema),
});

const ResultsSchema = z.object({
  category_name: z.string(),
  topics: z.array(TopicsSchema),
});

const PaginatedNotificationActionsSchema = z.object({
  count: z.number(),
  next: z.string().nullable(),
  previous: z.string().nullable(),
  results: z.array(ResultsSchema),
});

interface PaginatedNotificationActionsParams {
  page?: number;
  pageSize?: number;
}

export type PaginatedNotificationActions = z.infer<typeof PaginatedNotificationActionsSchema>;

// * Actions
// * This is an example of how to validate on runtime using zod
export const getNotificationActions = async (
  params: PaginatedNotificationActionsParams = { page: 1, pageSize: 999 }
) => {
  try {
    const { data } = await http.get<PaginatedNotificationActions>(ACTIONS_URL, { params });
    const result = PaginatedNotificationActionsSchema.safeParse(data);

    if (!result.success) {
      throw new Error(`Invalid response schema: ${result.error.message}`);
    }

    return result.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(`API Error: ${error.message}`);
    }
    throw error;
  }
};

// * Notifications
export const getInAppNotifications = async (page = 1, pageSize = 10) =>
  await http.get<PaginatedNotificationList>(IN_APP_URL, {
    params: { page_size: pageSize, page },
  });

export const postInAppNotification = async (notification: Notification) =>
  await http.post<Notification>(IN_APP_URL, notification);

export const getInAppNotificationById = async (id: number) =>
  await http.get<Notification>(IN_APP_BY_ID_URL(id));

export const patchInAppNotification = async (id: number) =>
  await http.patch<Notification>(IN_APP_BY_ID_URL(id));

// * Subscriptions

// * In App Subscriptions
export const getInAppSubscriptions = async (page = 1, pageSize = 50) =>
  await http.get<PaginatedSubscriptionCreateDeleteList>(IN_APP_SUBSCRIPTIONS_URL, {
    params: { page, page_size: pageSize },
  });

export const postInAppSubscription = async (subscriptions: SubscriptionCreateDelete[]) =>
  await http.post<SubscriptionCreateDelete[]>(IN_APP_SUBSCRIPTIONS_URL, subscriptions);

export const deleteInAppSubscription = async (subscriptions: SubscriptionCreateDelete[]) =>
  await http.delete<SubscriptionCreateDelete>(IN_APP_SUBSCRIPTIONS_URL, {
    data: subscriptions,
  });

// * Email Subscriptions
export const getEmailSubscriptions = async (page = 1, pageSize = 50) =>
  await http.get<PaginatedSubscriptionCreateDeleteList>(EMAIL_SUBSCRIPTIONS_URL, {
    params: { page, page_size: pageSize },
  });

export const postEmailSubscription = async (subscriptions: SubscriptionCreateDelete[]) =>
  await http.post<SubscriptionCreateDelete[]>(EMAIL_SUBSCRIPTIONS_URL, subscriptions);

export const deleteEmailSubscription = async (subscriptions: SubscriptionCreateDelete[]) =>
  await http.delete<SubscriptionCreateDelete>(EMAIL_SUBSCRIPTIONS_URL, {
    data: subscriptions,
  });
